import { Button, Input, Label } from 'components';
import { toaster } from 'helpers/toaster';
import { useTranslations } from 'next-intl';
import { useGithubData } from 'providers/IntegrationsProvider/useGithubData';
import { useGithubRepoCreate } from 'providers/IntegrationsProvider/useGithubRepoCreate';
import { ChangeEvent, FormEvent, useCallback, useState } from 'react';
import { notifyException } from 'services/appsignal';
import { useGithubContext } from '../_context/GithubContext';
import NewRepositoryPersonalToken from './NewRepositoryPersonalToken';
import { correctRepoFormat, getIsFormSendable } from './_utils';

const NewRepository = () => {
  const { data, refetch } = useGithubData();
  const t = useTranslations();
  const { toggleShowNewForm } = useGithubContext();

  const [repoName, setRepositoryName] = useState('');
  const [personalToken, setPersonalToken] = useState('');
  const [useSameToken, setUseSameToken] = useState(false);

  const { mutateAsync: githubRepoCreate } = useGithubRepoCreate({
    onSuccess: () => toaster.success(t('messages.success.creating_repo')),
    onError: (error: unknown) => {
      notifyException(error);
      toaster.error(t('messages.error.creating_repo'));
    }
  });

  const updateRepositoryName = (e: ChangeEvent<HTMLInputElement>) =>
    setRepositoryName(e.target.value);

  const updatePersonalToken = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setPersonalToken(e.target.value),
    []
  );

  const toggleUseSameToken = useCallback(() => setUseSameToken((oldState) => !oldState), []);

  const showRepoNameWarning = repoName.length > 0 && !correctRepoFormat(repoName);

  const isFormSendable =
    getIsFormSendable({
      repoName,
      personalToken,
      useSameToken
    }) &&
    ((!useSameToken && personalToken.length > 0) || useSameToken);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await githubRepoCreate({
      repoName,
      personalToken,
      useSameToken
    });
    refetch();
    toggleShowNewForm();
  };

  return (
    <form className="github__sidebox" onSubmit={onSubmit}>
      <span className="githhub__sidebox__logo ticon-github"></span>
      {!!data?.githubRepos?.length && (
        <span className="github__sidebox__close ticon-cancel" onClick={toggleShowNewForm}></span>
      )}
      <Label>{t('integrations.github.repository')}</Label>
      <Input
        name="new-repository-name"
        type="text"
        value={repoName}
        placeholder={t('integrations.github.repo_name_format')}
        onChange={updateRepositoryName}
      />
      {showRepoNameWarning ? (
        <small className="github__sidebox__note">
          <span className="ticon-info"></span> {t('integrations.github.repo_name_format_warning')}
        </small>
      ) : null}
      <NewRepositoryPersonalToken
        useSameToken={useSameToken}
        toggleUseSameToken={toggleUseSameToken}
        personalToken={personalToken}
        updatePersonalToken={updatePersonalToken}
      />
      <div className="mt-4 flex justify-center">
        <Button size="medium" disabled={!isFormSendable}>
          {t('integrations.github.add_repository')}
        </Button>
      </div>
    </form>
  );
};

export default NewRepository;
